import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import type {
  AllowanceDataFragment,
  BulkAssignEmployeesTimeoffPolicyMappingsInput,
} from 'generated/resources/graphql'
import {
  ProfileFetchPolicyChangeDataDocument,
  ProfilePageDocument,
  TimeoffPolicyAddEmployeeBulkAssignEmployeesDocument,
  UpdateEmployeeDocument,
  UpdateInitialPolicyAssignmentDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Form from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { BusinessError } from 'modules/core/lib/errors'
import { handleResult } from 'modules/core/lib/handleMutation'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import TimeoffForm from 'modules/employees/lib/forms/EmployeeForm/Timeoff'
import AllowanceMapper from 'modules/timeoff/components/TimeoffAllowances/AllowanceMapper'
import { hasPolicyTemporality } from 'modules/timeoff/lib/features/TimeoffFeatures'
import timeoffPolicies from 'modules/timeoff/store/collections/timeoffPolicies/policies'

const createTimeoffForm = (
  timeoffManager: { id: string; fullName: string } | null,
  timeoffPolicyId: string | null,
  tenureStartDate: string | null
) => {
  const effectivePolicyId = hasPolicyTemporality() ? null : timeoffPolicyId

  return new TimeoffForm({
    timeoff_manager_id: timeoffManager?.id ?? undefined,
    timeoff_policy_id: effectivePolicyId ?? undefined,
    tenure_start_date: tenureStartDate ?? undefined,
  })
}

interface Props {
  employeeId: string
  accessId: string
  employeeFullName: string
  timeoffManager: { id: string; fullName: string } | null
  timeoffPolicyId: string | null
  tenureStartDate: string | null
  companyId: string
  canEditManager: boolean
  readonly?: boolean
}

const TimeoffManagerSelector = ({
  employeeId,
  accessId,
  employeeFullName,
  timeoffManager,
  timeoffPolicyId,
  tenureStartDate,
  companyId,
  canEditManager,
  readonly,
}: Props) => {
  const [timeoffForm, setTimeoffForm] = useState<TimeoffForm>(
    createTimeoffForm(timeoffManager, timeoffPolicyId, tenureStartDate)
  )
  const [selectedTimeoffManagerId, setSelectedTimeoffManagerId] = useState<string | null>(
    timeoffManager?.id ?? null
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { has_tenure_start_date } = useFeatures(['tenure_start_date'])
  const [showMapperModal, setShowMapperModal] = useState(false)
  const [mappings, setMappings] = useState<BulkAssignEmployeesTimeoffPolicyMappingsInput[]>([])
  const [currentPolicyAllowances, setCurrentPolicyAllowances] = useState<AllowanceDataFragment[]>(
    []
  )
  const [newPolicyAllowances, setNewPolicyAllowances] = useState<AllowanceDataFragment[]>([])
  const [currentPolicyIdForMapper, setCurrentPolicyIdForMapper] = useState<string | null>(null)
  const [pendingNewPolicyId, setPendingNewPolicyId] = useState<string | null>(null)

  const [fetchPolicyChangeData] = useLazyQuery(ProfileFetchPolicyChangeDataDocument)

  const [updateEmployee] = useMutation(UpdateEmployeeDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
    refetchQueries: [ProfilePageDocument],
  })
  const [updateInitialAssignment] = useMutation(UpdateInitialPolicyAssignmentDocument, {
    extractorFn: (data) => data.timeoffMutations.updateInitialTimeoffPolicyAssignment,
    refetchQueries: [ProfilePageDocument],
  })
  const [bulkAssignEmployees] = useMutation(TimeoffPolicyAddEmployeeBulkAssignEmployeesDocument, {
    extractorFn: (data) => data.timeoffMutations.bulkAssignEmployeesTimeoffPolicy,
    refetchQueries: [ProfilePageDocument],
  })

  useEffect(() => {
    setTimeoffForm(createTimeoffForm(timeoffManager, timeoffPolicyId, tenureStartDate))
    setSelectedTimeoffManagerId(timeoffManager?.id ?? null)
  }, [timeoffManager?.id, timeoffPolicyId, tenureStartDate])

  const isReadonly = readonly || !canEditManager
  const showPolicySelector = !hasPolicyTemporality() && canEditManager

  const options = timeoffPolicies.models.map((timeoffPolicy) => ({
    label: timeoffPolicy.attributes.name,
    value: timeoffPolicy.attributes.id,
  }))

  const submitWithMappings = async (policyId: string) => {
    setIsSubmitting(true)
    await asyncAction(async () => {
      const result = await bulkAssignEmployees({
        variables: {
          employeeIds: [employeeId],
          mappings,
          policyId,
        },
      })
      const handled = handleResult(result, () => {})
      if (!handled.success) {
        const messages = handled.errors
          .map((e) => ('messages' in e ? e.messages.join(', ') : e.message))
          .join('. ')
        throw new BusinessError(messages || i18n.t('errors.generic_error'))
      }

      await updateEmployee({
        variables: {
          id: employeeId,
          accessId: accessId,
          timeoffManagerId: timeoffForm.get('timeoff_manager_id').value || null,
        },
      })

      const managerId = timeoffForm.get('timeoff_manager_id').value
      setTimeoffForm(
        createTimeoffForm(
          managerId ? { id: managerId, fullName: '' } : null,
          policyId,
          timeoffForm.get('tenure_start_date').value ?? null
        )
      )
      setSelectedTimeoffManagerId(managerId ?? null)
      setShowMapperModal(false)
    }).finally(() => setIsSubmitting(false))
  }

  function onSubmit() {
    track(trackingEvents.employee.profile.editEmployeeProfileTimeOffSubmit)

    const newPolicyId = timeoffForm.get('timeoff_policy_id').value
    const policyChanged = newPolicyId && newPolicyId !== timeoffPolicyId

    if (policyChanged && timeoffPolicyId) {
      setIsSubmitting(true)

      void fetchPolicyChangeData({
        variables: {
          employeeId,
          currentPolicyId: timeoffPolicyId,
          newPolicyId,
        },
      }).then(({ data }) => {
        const employeeIncidences =
          data?.employees.employee?.timeoffAllowanceIncidences.filter(
            (incidence) => incidence.timeoffAllowance?.timeoffPolicyId === timeoffPolicyId
          ) ?? []

        const currentAllowances = data?.timeoff.currentPolicy?.allowances ?? []
        const newAllowances = data?.timeoff.newPolicy?.allowances ?? []

        if (
          employeeIncidences.length > 0 &&
          currentAllowances.length > 0 &&
          newAllowances.length > 0
        ) {
          setCurrentPolicyAllowances(currentAllowances)
          setNewPolicyAllowances(newAllowances)
          setCurrentPolicyIdForMapper(timeoffPolicyId)
          setPendingNewPolicyId(newPolicyId)
          setMappings([])
          setShowMapperModal(true)
          setIsSubmitting(false)
        } else {
          void submitWithoutMapper(newPolicyId)
        }
      })
      return
    }

    return submitWithoutMapper(newPolicyId)
  }

  function submitWithoutMapper(newPolicyId: string | undefined) {
    setIsSubmitting(true)
    return asyncAction(async () => {
      if (newPolicyId && newPolicyId !== timeoffPolicyId) {
        const result = await updateInitialAssignment({
          variables: {
            employeeId,
            timeoffPolicyId: newPolicyId,
          },
        })
        const handled = handleResult(result, () => {})
        if (!handled.success) {
          const messages = handled.errors
            .map((e) => ('messages' in e ? e.messages.join(', ') : e.message))
            .join('. ')
          throw new BusinessError(messages || i18n.t('errors.generic_error'))
        }
      }

      await updateEmployee({
        variables: {
          id: employeeId,
          accessId: accessId,
          timeoffManagerId: timeoffForm.get('timeoff_manager_id').value || null,
        },
      })

      const managerId = timeoffForm.get('timeoff_manager_id').value
      setTimeoffForm(
        createTimeoffForm(
          managerId ? { id: managerId, fullName: '' } : null,
          newPolicyId ?? timeoffPolicyId,
          timeoffForm.get('tenure_start_date').value ?? null
        )
      )
      setSelectedTimeoffManagerId(managerId ?? null)
    }).finally(() => setIsSubmitting(false))
  }

  return (
    <>
      <Form onSubmit={onSubmit}>
        {showPolicySelector && (
          <FormRow>
            <Select
              field={timeoffForm.get('timeoff_policy_id')}
              label={i18n.t('time_off.timeoff_policy_legend')}
              options={options}
            />
          </FormRow>
        )}
        <FormRow>
          <EmployeeSelectorV2
            label={i18n.t('time_off.timeoff_manager_legend')}
            disabled={isReadonly}
            forcedFilters={{ companyId, excludeIds: [String(employeeId)] }}
            value={selectedTimeoffManagerId ?? undefined}
            onChange={(timeOffManagerId) => {
              setSelectedTimeoffManagerId(timeOffManagerId ?? null)
              timeoffForm
                .get('timeoff_manager_id')
                .set(timeOffManagerId ? String(timeOffManagerId) : null)
            }}
          />
        </FormRow>
        {has_tenure_start_date && (
          <FormRow>
            <DayPicker
              readonly={isReadonly}
              field={timeoffForm.get('tenure_start_date')}
              label={i18n.t('time_off.tenure_start_date')}
              placeholder={i18n.t('time_off.contract_start_date')}
              withNullLabel={i18n.t('time_off.use_contract_start_date')}
              withNull
            />
          </FormRow>
        )}
        {timeoffForm.isDirty && (
          <FormRow footer>
            <Button submit disabled={isSubmitting}>
              {i18n.t('common.submit')}
            </Button>
          </FormRow>
        )}
      </Form>
      {showMapperModal && currentPolicyIdForMapper && pendingNewPolicyId && (
        <Modal
          illustration="document-edit"
          title={i18n.t('settings.timeoff_policies.move_employee_modal.title', {
            employee: employeeFullName,
          })}
          description={i18n.t('settings.timeoff_policies.move_employee_modal.description_complete')}
          onClose={() => setShowMapperModal(false)}
          footer={
            <Button
              disabled={isSubmitting}
              onClick={() => void submitWithMappings(pendingNewPolicyId)}
            >
              {i18n.t('common.submit')}
            </Button>
          }
        >
          <AllowanceMapper
            policyId={currentPolicyIdForMapper}
            allowancesToMap={currentPolicyAllowances}
            timeoffPolicyAllowances={newPolicyAllowances}
            onMappingsChange={setMappings}
          />
        </Modal>
      )}
    </>
  )
}

export default observer(TimeoffManagerSelector)
